/**
 * T114-F5-j: Batch Tag Dialog
 * Add or remove tags from selected transactions with preview.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { TagInput } from '@/components/ui/tag-input';
import { Eye, Tags } from 'lucide-react';
import { useAppDispatch } from '@/features/hooks';
import { batchTagTransactions } from '@/features/transactions/transactionsSlice';
import { useToast } from '@/hooks/use-toast';
import type { BatchOperationResult } from '@/lib/types';

interface BatchTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  existingTags: string[];
  onSuccess: () => void;
}

export function BatchTagDialog({
  open,
  onOpenChange,
  selectedIds,
  existingTags,
  onSuccess,
}: BatchTagDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const [addTags, setAddTags] = useState<string[]>([]);
  const [removeTags, setRemoveTags] = useState<string[]>([]);

  const [preview, setPreview] = useState<BatchOperationResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const hasChanges = addTags.length > 0 || removeTags.length > 0;

  const handlePreview = async () => {
    if (!hasChanges) return;
    setIsPreviewLoading(true);
    try {
      const result = await dispatch(
        batchTagTransactions({
          transactionIds: selectedIds,
          ...(addTags.length > 0 && { addTags }),
          ...(removeTags.length > 0 && { removeTags }),
          dryRun: true,
        })
      ).unwrap();
      setPreview(result);
    } catch (err) {
      toast({ title: 'Preview failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!hasChanges) return;
    setIsApplying(true);
    try {
      const result = await dispatch(
        batchTagTransactions({
          transactionIds: selectedIds,
          ...(addTags.length > 0 && { addTags }),
          ...(removeTags.length > 0 && { removeTags }),
          dryRun: false,
        })
      ).unwrap();
      toast({
        title: 'Tags updated',
        description: `${result.affected} transaction${result.affected !== 1 ? 's' : ''} updated`,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      toast({ title: 'Tag update failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsApplying(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setAddTags([]);
    setRemoveTags([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="batch-tag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Tag {selectedIds.length} Transaction{selectedIds.length !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>Add or remove tags from selected transactions.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-5 py-2">
            {/* Add Tags */}
            <div className="space-y-2" data-testid="batch-add-tags-input">
              <Label>Add Tags</Label>
              <TagInput
                value={addTags}
                onChange={setAddTags}
                suggestions={existingTags}
                placeholder="Type a tag..."
              />
            </div>

            {/* Remove Tags */}
            <div className="space-y-2" data-testid="batch-remove-tags-input">
              <Label>Remove Tags</Label>
              <TagInput
                value={removeTags}
                onChange={setRemoveTags}
                suggestions={existingTags}
                placeholder="Type a tag to remove..."
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-2 max-h-[300px] overflow-y-auto" data-testid="batch-tag-preview">
            <p className="text-sm font-medium">
              {preview.affected} transaction{preview.affected !== 1 ? 's' : ''} will be affected
            </p>
            {isPreviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              preview.results?.slice(0, 50).map((r) => (
                <div key={r.transactionId} className="p-2 rounded border text-sm">
                  <p className="font-medium break-words">{r.description}</p>
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!preview ? (
            <Button onClick={handlePreview} disabled={!hasChanges || isPreviewLoading}>
              <Eye className="mr-2 h-4 w-4" />
              {isPreviewLoading ? 'Loading...' : 'Preview'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back
              </Button>
              <Button onClick={handleApply} disabled={isApplying}>
                {isApplying ? 'Applying...' : 'Apply'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
